/**
 * Pure SVG geometry/text helpers for `ExplorationMap.tsx` and
 * `ExplorationMapKey.tsx` — hex corner math, hex→polygon-points strings, an
 * alpha-blended hex color for swatches, the coverage fill-opacity levels
 * shared by the map and the legend, and the area-age word the key row
 * shows. No React, no layout logic (that's `hexLayout.ts`) — split out
 * purely to keep the component files under the project's file-size
 * guidance. v3 (design review): the map itself carries no text and no
 * per-area hue, so the label-wrapping/pill-sizing/area-hue helpers this
 * module used to export are gone (net delete, not renamed).
 *
 * @module features/exploration/map/svgGeometry
 */

export type Point = { x: number; y: number };

/**
 * Fill-opacity levels for tiles, shared by the map (`ExplorationMap.tsx`)
 * and the legend (`ExplorationMapLegend.tsx`) so their swatches can never
 * drift from the real on-map values (Decision 9 v3, round 3 review).
 * `covered` has no entry — it's always fully opaque (1).
 */
export const COVERAGE_ALPHA = {
  uncovered: 0.18,
  stale: 0.45,
  fogged: 0.18,
} as const;

/**
 * Appends an alpha channel to a 6-digit hex color (`#rrggbb` -> `#rrggbbaa`)
 * for a swatch that needs one composited color string (the key/legend's
 * plain `backgroundColor` divs) rather than a separate SVG
 * `fillOpacity`/`strokeOpacity` attribute (which the map's own tiles use
 * directly, per Decision 12).
 */
export function withAlpha(hex: string, alpha: number): string {
  const clamped = Math.max(0, Math.min(1, alpha));
  const alphaHex = Math.round(clamped * 255)
    .toString(16)
    .padStart(2, '0');
  return `${hex}${alphaHex}`;
}

/** Pointy-top hex corner offsets around a center, in drawing order (angles -30, 30, 90, 150, 210, 270). */
export function hexCorners(radius: number): Point[] {
  return Array.from({ length: 6 }, (_, i) => {
    const angle = (Math.PI / 180) * (60 * i - 30);
    return { x: radius * Math.cos(angle), y: radius * Math.sin(angle) };
  });
}

export function hexPoints(center: Point, radius: number): string {
  return hexCorners(radius)
    .map((corner) => `${center.x + corner.x},${center.y + corner.y}`)
    .join(' ');
}

function daysSince(iso: string): number {
  return Math.floor((Date.now() - new Date(iso).getTime()) / (24 * 60 * 60 * 1000));
}

/** "today" / "3d" / "never" — the area key's age column. */
export function ageWord(lastExploredAt: string | null): string {
  if (!lastExploredAt) return 'never';
  const days = daysSince(lastExploredAt);
  return days <= 0 ? 'today' : `${days}d`;
}
